import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar, Clock, UserCheck, UserX, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClassService } from "@/services/classService";
import { AttendanceService, AttendanceRecord } from "@/services/attendanceService";
import { Enrollment, Class, Student } from "@/types/index";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

interface AttendanceTrackerProps {
  classId: string;
  organizationId: string;
  enrollments?: Enrollment[];
}

const AttendanceTracker: React.FC<AttendanceTrackerProps> = ({
  classId,
  organizationId,
  enrollments: initialEnrollments,
}) => {
  const [classDetails, setClassDetails] = useState<Class | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>(initialEnrollments || []);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("take");
  const [attendanceStats, setAttendanceStats] = useState<{
    totalSessions: number;
    studentStats: Record<
      string,
      { present: number; absent: number; late: number; excused: number }
    >;
  }>({ totalSessions: 0, studentStats: {} });

  const normalizeEnrollments = (data: any[]): Enrollment[] => {
    return data.map(item => ({
      ...item,
      status: item.status === 'waitlist' ? 'waitlisted' : 
             (item.status === 'active' || item.status === 'withdrawn' || item.status === 'waitlisted' ? 
              item.status : 'active')
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [classData, enrollmentsData] = await Promise.all([
          ClassService.getClassById(classId),
          !initialEnrollments ? ClassService.getClassEnrollments(classId) : Promise.resolve([]),
        ]);

        setClassDetails(classData);
        if (!initialEnrollments) {
          setEnrollments(normalizeEnrollments(enrollmentsData));
        }
      } catch (error) {
        console.error("Error fetching class data:", error);
        toast.error("Failed to load class information");
      } finally {
        setIsLoading(false);
      }
    };

    if (classId) {
      fetchData();
    }
  }, [classId, initialEnrollments]);

  const handleAttendanceChange = async (
    studentId: string,
    status: "present" | "absent" | "late" | "excused",
    notes?: string
  ) => {
    if (!classId || !selectedDate || !organizationId) return;

    const formattedDate = format(selectedDate, "yyyy-MM-dd");

    const record = {
      class_id: classId,
      student_id: studentId,
      organization_id: organizationId,
      date: formattedDate,
      status,
      notes: notes || "",
    };

    const result = await AttendanceService.recordAttendance(record);
    if (result) {
      setAttendanceRecords((prev) => {
        const existing = prev.findIndex((r) => r.student_id === studentId);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = result;
          return updated;
        } else {
          return [...prev, result];
        }
      });
    }
  };

  const handleBulkAttendance = async (status: "present" | "absent") => {
    if (!classId || !selectedDate || !organizationId || enrollments.length === 0) return;

    const formattedDate = format(selectedDate, "yyyy-MM-dd");

    const records = enrollments.map((enrollment) => ({
      class_id: classId,
      student_id: enrollment.student_id,
      organization_id: organizationId,
      date: formattedDate,
      status,
      notes: status === "absent" ? "Bulk marked as absent" : "",
    }));

    const success = await AttendanceService.bulkRecordAttendance(records);
    if (success) {
      const updatedRecords = await AttendanceService.getAttendanceByClass(
        classId,
        formattedDate
      );
      setAttendanceRecords(updatedRecords);
    }
  };

  const getStudentAttendanceStatus = (studentId: string): AttendanceRecord | undefined => {
    return attendanceRecords.find((record) => record.student_id === studentId);
  };

  const renderStatusBadge = (status?: string) => {
    if (!status) return null;

    switch (status) {
      case "present":
        return <Badge className="bg-green-500">Present</Badge>;
      case "absent":
        return <Badge className="bg-red-500">Absent</Badge>;
      case "late":
        return <Badge className="bg-yellow-500">Late</Badge>;
      case "excused":
        return <Badge className="bg-blue-500">Excused</Badge>;
      default:
        return null;
    }
  };

  const calculateAttendanceRate = (studentId: string) => {
    const stats = attendanceStats.studentStats[studentId];
    if (!stats) return 0;

    const total = stats.present + stats.absent + stats.late + stats.excused;
    if (total === 0) return 0;

    return Math.round(((stats.present + stats.late) / total) * 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-muted-foreground">Loading attendance tracker...</p>
      </div>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-bold">
            Attendance Tracker{" "}
            {classDetails && <span className="font-normal">- {classDetails.name}</span>}
          </CardTitle>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-auto pl-3 h-8 flex gap-2">
                <Calendar className="h-4 w-4" />
                {format(selectedDate, "MMMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                disabled={(date) => date > new Date()}
              />
            </PopoverContent>
          </Popover>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs
          defaultValue="take"
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="mb-4 grid grid-cols-2">
            <TabsTrigger value="take">Take Attendance</TabsTrigger>
            <TabsTrigger value="report">Attendance Report</TabsTrigger>
          </TabsList>

          <TabsContent value="take" className="space-y-4">
            <div className="flex justify-between mb-4">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => handleBulkAttendance("present")}
              >
                <UserCheck className="h-4 w-4" /> Mark All Present
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => handleBulkAttendance("absent")}
              >
                <UserX className="h-4 w-4" /> Mark All Absent
              </Button>
            </div>

            {enrollments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  No students enrolled in this class yet.
                </p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[150px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enrollments.map((enrollment) => {
                      const student = enrollment.student;
                      const attendanceRecord = getStudentAttendanceStatus(
                        enrollment.student_id
                      );

                      return (
                        <TableRow key={enrollment.student_id}>
                          <TableCell>
                            <div className="font-medium">
                              {student?.first_name} {student?.last_name}
                            </div>
                          </TableCell>
                          <TableCell>
                            {renderStatusBadge(attendanceRecord?.status)}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={attendanceRecord?.status || ""}
                              onValueChange={(value) =>
                                handleAttendanceChange(
                                  enrollment.student_id,
                                  value as any,
                                  attendanceRecord?.notes
                                )
                              }
                            >
                              <SelectTrigger className="w-[130px]">
                                <SelectValue
                                  placeholder={
                                    <span className="text-muted-foreground">
                                      Set status...
                                    </span>
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="present">Present</SelectItem>
                                <SelectItem value="absent">Absent</SelectItem>
                                <SelectItem value="late">Late</SelectItem>
                                <SelectItem value="excused">Excused</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="report">
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-green-50">
                  <CardContent className="p-4 flex flex-col items-center justify-center">
                    <UserCheck className="h-8 w-8 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-center">Present Rate</p>
                    <p className="text-2xl font-bold">
                      {attendanceStats.totalSessions > 0
                        ? Math.round(
                            (Object.values(attendanceStats.studentStats).reduce(
                              (acc, stats) => acc + stats.present,
                              0
                            ) /
                              (attendanceStats.totalSessions *
                                enrollments.length || 1)) *
                              100
                          )
                        : 0}
                      %
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-red-50">
                  <CardContent className="p-4 flex flex-col items-center justify-center">
                    <UserX className="h-8 w-8 text-red-500 mb-2" />
                    <p className="text-sm font-medium text-center">Absence Rate</p>
                    <p className="text-2xl font-bold">
                      {attendanceStats.totalSessions > 0
                        ? Math.round(
                            (Object.values(attendanceStats.studentStats).reduce(
                              (acc, stats) => acc + stats.absent,
                              0
                            ) /
                              (attendanceStats.totalSessions *
                                enrollments.length || 1)) *
                              100
                          )
                        : 0}
                      %
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-blue-50">
                  <CardContent className="p-4 flex flex-col items-center justify-center">
                    <Calendar className="h-8 w-8 text-blue-500 mb-2" />
                    <p className="text-sm font-medium text-center">Total Classes</p>
                    <p className="text-2xl font-bold">{attendanceStats.totalSessions}</p>
                  </CardContent>
                </Card>

                <Card className="bg-purple-50">
                  <CardContent className="p-4 flex flex-col items-center justify-center">
                    <Clock className="h-8 w-8 text-purple-500 mb-2" />
                    <p className="text-sm font-medium text-center">Total Students</p>
                    <p className="text-2xl font-bold">{enrollments.length}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="rounded-md border mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Present</TableHead>
                      <TableHead>Absent</TableHead>
                      <TableHead>Late</TableHead>
                      <TableHead>Excused</TableHead>
                      <TableHead>Attendance Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enrollments.map((enrollment) => {
                      const student = enrollment.student;
                      const stats =
                        attendanceStats.studentStats[enrollment.student_id] || {
                          present: 0,
                          absent: 0,
                          late: 0,
                          excused: 0,
                        };
                      const attendanceRate = calculateAttendanceRate(
                        enrollment.student_id
                      );

                      return (
                        <TableRow key={enrollment.student_id}>
                          <TableCell>
                            <div className="font-medium">
                              {student?.first_name} {student?.last_name}
                            </div>
                          </TableCell>
                          <TableCell>{stats.present}</TableCell>
                          <TableCell>{stats.absent}</TableCell>
                          <TableCell>{stats.late}</TableCell>
                          <TableCell>{stats.excused}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span
                                className={`font-medium ${
                                  attendanceRate >= 80
                                    ? "text-green-600"
                                    : attendanceRate >= 60
                                    ? "text-yellow-600"
                                    : "text-red-600"
                                }`}
                              >
                                {attendanceRate}%
                              </span>
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${
                                    attendanceRate >= 80
                                      ? "bg-green-500"
                                      : attendanceRate >= 60
                                      ? "bg-yellow-500"
                                      : "bg-red-500"
                                  }`}
                                  style={{ width: `${attendanceRate}%` }}
                                ></div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AttendanceTracker;
